import React, { useContext, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'found';
import * as Sentry from '@sentry/browser';
import SessionTimeout from './SessionTimeout';
import { utils as syoUtils } from '@tildev-corp/syo-bilan';

import ThemeModeContext from './ui/useDarkMode';
import theme from './ui/theme';
import CurrentUserContext from './CurrentUserContext';
import FolderIcon from '@mui/icons-material/FolderShared';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import ChartsIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import DevicesIcon from '@mui/icons-material/SettingsCell';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import GradeIcon from '@mui/icons-material/Grade';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ColorizeIcon from '@mui/icons-material/Colorize';
import LeakAddIcon from '@mui/icons-material/LeakAdd';
import ArchiveIcon from '@mui/icons-material/Archive';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import logo from './ui/logo-small.png';
import { Dialog } from './ui/Modal';
import { ErrorBox, ErrorLink, StyledContent } from './ui/ErrorBox';
import { Widget } from '@typeform/embed-react';

import '@typeform/embed/build/css/widget.css';

import faq from './ui/faq.svg';
import {
  hasIndividualOrEquivalentPerm,
  hasLegacyPerm,
  INDIVIDUAL_PERMISSIONS,
  isFormation,
  orgaWithAAD,
  useInterval,
} from './utils';
import { DateTime, Interval } from 'luxon';
import { toast } from 'react-toastify';
import http, { HTTPError, signOut } from './http';
import Button from './ui/Button';

const ButtonNav = styled(Button)`
  border: none;
  width: 100%;

  svg {
    fill: white;
  }
`;

const SideNav = styled.nav`
  background: linear-gradient(0deg, ${theme.darkBlue} 35%, ${theme.darkGradientBlue} 100%);
  box-shadow: 0px 0 0px ${theme.grey5};
  z-index: 10;
  position: fixed;
  height: 100vh;
  top: 0px;
  display: flex;
  flex-direction: column;
  //justify-content: space-between;
  gap: 0px;
  &.close {
    width: ${theme.menuWidth};
  }
  &.open {
    width: 300px;
  }
  @media (max-width: 800px) {
    display: none;
  }

  .icon {
    color: ${theme.text};
    transition: transform 0.3s ease-in-out;
    transform: rotate(-90deg);
    &.close {
      transform: rotate(90deg);
    }
  }

  .scrollbarStyle::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .scrollbarStyle::-webkit-scrollbar {
    width: 6px;
    background-color: transparent;
  }

  .scrollbarStyle::-webkit-scrollbar-thumb {
    background-color: #000000;
  }
`;

const SideNavMobile = styled.nav`
  display: ${props => (props.menuIsOpen ? 'block' : 'none')};
  background: linear-gradient(0deg, ${theme.darkBlue} 35%, ${theme.darkGradientBlue} 100%);
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 12;

  .logo {
    width: 5rem;
    height: 5rem;
    margin: auto;
  }
  @media (min-width: 801px) {
    display: none;
  }
`;

const SideNavContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  height: min-content;

  &.end {
    border-top: ${theme.darkBlue} solid 2px;
    justify-self: flex-end !important;
  }
`;

const SideNavContentScrollable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  overflow-y: scroll;
  overflow-x: clip;
  border-top: ${theme.darkGradientBlue} solid 2px;
  height: 100%;
`;

const SideNavContentMobile = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  height: 100vh;
  overflow-y: scroll;
  margin: auto;
  padding-top: ${theme.large};
  padding-bottom: ${theme.large};
  @media (max-width: 550px) {
    width: 65%;
  }
  @media (max-width: 450px) {
    width: 80%;
  }
  @media (max-width: 350px) {
    width: 90%;
  }
`;

const SideNavContentLogoAltMobile = styled.div`
  position: fixed;
  top: ${theme.thin};
  left: ${theme.thin};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SideNavContentAltMobile = styled.div`
  position: fixed;
  bottom: ${theme.thin};
  right: ${theme.thin};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconContainer = styled.span`
  padding-top: 12px;
  padding-right: 12px;
  padding-bottom: 12px;
  padding-left: 12px;
  display: inline-flex;
  transition: background-color 0s ease-in-out;
  svg {
    fill: #96a3b3;
    transition: transform 0.2s ease-in-out;
    transform: scale(1);
  }
  &:hover {
    svg {
      transform: scale(1.1);
    }
  }
`;
const IconContainerSide = styled(IconContainer)`
  padding-top: 4px;
  padding-bottom: 4px;
`;

const SideLink = styled(Link)`
  padding: 0.25rem ${theme.thin};
  display: block;
  color: #96a3b3;
  position: relative;
`;

const UserMenu = styled(SideLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${theme.blue};
  color: ${theme.backgroundColor};
  margin: 4px;
  height: 40px !important;
  width: 40px !important;
  :hover {
    color: ${theme.backgroundColor};
  }
  @media (max-width: 800px) {
    margin: 0;
    margin-left: ${theme.small};
  }
`;

const ToggleMenu = styled.button`
  z-index: 12;
  position: fixed;
  bottom: ${theme.thin};
  left: ${theme.thin};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background-color: ${theme.blue};
  color: ${theme.backgroundColor};
  height: 40px !important;
  width: 40px !important;
  :hover {
    color: ${theme.backgroundColor};
  }
  @media (max-width: 800px) {
    display: block;
  }
  @media (min-width: 801px) {
    display: none;
  }
`;

const PageLink = styled(SideLink)`
  transition: background-color 0s ease-in-out;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  gap: 10px;
  /*:hover:after {
    content: attr(aria-label);
    position: absolute;
    left: 4.1em;
    top: 50%;
    transform: translateY(-50%); /* on centre horizontalement  */
  /*z-index: 1; /* pour s'afficher au dessus des éléments en position relative */
  /*white-space: nowrap; /* on interdit le retour à la ligne*/
  /*padding: 5px 14px;
    background: ${theme.darkBlue};
    color: ${theme.white};
    border-radius: 4px;
    font-weight: normal;
  }*/

  p {
    margin: 0px;
  }

  :hover,
  :focus {
    color: ${theme.white};
    text-decoration: none;
    border-radius: 3px;
  }

  &:hover {
    svg {
      transform: scale(1.2);
    }
  }

  &.active > ${IconContainer} {
    color: ${theme.white};
    border-radius: 10px;
    background-color: #485b70;
    & svg {
      fill: ${theme.white};
    }
  }
`;

const PageLinkMobile = styled(SideLink)`
  transition: background-color 0s ease-in-out;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: ${theme.large};
  :hover,
  :focus {
    color: ${theme.white};
    font-weight: bold;
    text-decoration: none;
    border-radius: 3px;
  }

  &.active {
    color: ${theme.white};
    border-radius: 10px;
    background-color: #485b70;
  }
`;

// const ManagerSideLink = styled(PageLink)`
//   position: absolute;
//   bottom: 70px;
//   left: 0;
//   height: 40px !important;
//   width: 40px !important;
// `;

const FlexContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.backgroundColor};
  color: ${theme.grey2};
  /* padding-bottom: ${theme.small}; */
  padding-bottom: 4rem;
  &.openShell {
    margin-left: 300px;
    width: calc(100% - 300px);
  }
  &.closeShell {
    margin-left: 4rem;
    width: calc(100% - 4rem);
  }
  &:has(.medecine) {
    background-color: ${theme.subMenu} !important;
  }
  @media (max-width: 800px) {
    margin-left: 0 !important;
    width: 100% !important;
  }
`;

const Support = styled.div`
  color: #96a3b3;
  position: relative;
  //margin: ${theme.small} ${theme.small} 0 ${theme.small};
  padding: 0.25rem ${theme.thin};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  gap: 10px;

  p {
    margin: 0px;
  }

  :hover {
    cursor: pointer;
    color: ${theme.white};
    text-decoration: none;
    border-radius: 3px;
  }
  svg {
    transition: transform 0.2s ease-in-out;
    transform: scale(1);
  }
  &:hover {
    svg {
      transform: scale(1.2);
    }
  }

  & ${PageLink} {
    padding-left: 0px !important;
  }
`;

const SupportMobile = styled.div`
  display: block;
  color: #96a3b3;
  position: relative;
  margin-left: ${theme.small};

  :hover {
    cursor: pointer;
    color: ${theme.white};
    font-weight: bold;
    text-decoration: none;
    border-radius: 3px;
  }
  svg {
    transition: transform 0.2s ease-in-out;
    transform: scale(1);
  }
  &:hover {
    svg {
      transform: scale(1.2);
    }
  }
`;

const SupportContent = styled.div`
  display: flex;
  position: relative;
  color: ${theme.grey2};
  margin-top: ${theme.medium};
  align-items: end;

  h3 {
    color: ${theme.grey1} !important;
  }

  div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: ${theme.medium};
  }
  img {
    height: 153px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: center;
    div {
      margin-left: 0px;
      margin-top: ${theme.medium};
    }
    img {
      height: 200px;
    }
  }
`;

const PastilleNewItem = styled.div`
  position: absolute;
  background-color: ${props => theme[props.color] || theme.orange};
  border-radius: 50px;
  top: 8px;
  left: 36px;
  height: 0.6rem;
  width: 0.6rem;
  @media (max-width: 801px) {
    top: 2px;
    left: 16px;
  }
`;

class Content extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error) {
    // TODO: decorate with error details ?
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <StyledContent>
          <ErrorBox>
            <h3>Une erreur est survenue lors du traitement de votre requête</h3>
            <p>Nos équipes ont été notifiées et s'occuperont de votre problème au plus vite.</p>
            <ErrorLink
              to='/'
              onClick={() => {
                this.setState({ hasError: false });
              }}
            >
              Retourner à l'accueil
            </ErrorLink>
          </ErrorBox>
        </StyledContent>
      );
    }
    return <StyledContent className={this.props.className}>{this.props.children}</StyledContent>;
  }
}

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: 10px;
  margin-top: 0.5rem;
  margin-bottom: 0px;

  p {
    margin: 0px;
    color: white;
  }
`;

const Img = styled.img`
  display: block;
  padding: 10px;
  border-radius: 50%;
  width: 4rem;
  height: 4rem;

  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

function SupportOnline({ openShell, mobile = false }) {
  const [online, setOnline] = useState(false);
  const [supportShow, setSupport] = useState(false);

  const start = DateTime.fromISO('09:30');
  const finish = DateTime.fromISO('18:10');

  const isOnline = () => {
    const local = DateTime.local();
    const i = Interval.fromDateTimes(start, finish);
    setOnline(i.contains(local));
  };

  const setSupportShow = () => {
    setSupport(!supportShow);
  };

  useEffect(() => {
    isOnline();
  }, []);

  useInterval(600000, isOnline);

  return (
    <>
      {mobile ? (
        <SupportMobile aria-label='Assistance Syopé' onClick={() => setSupportShow()}>
          <HelpOutlineIcon />
          <PastilleNewItem color={online ? 'green' : 'orange'} />
        </SupportMobile>
      ) : (
        <Support aria-label='Assistance Syopé' onClick={() => setSupportShow()}>
          <IconContainerSide>
            <HelpOutlineIcon />
            <PastilleNewItem color={online ? 'green' : 'orange'} />
          </IconContainerSide>
          {openShell && <p>Assistance Syopé</p>}
        </Support>
      )}
      <Dialog
        isOpen={supportShow}
        toggleDialog={() => setSupportShow()}
        title='Assistance Syopé'
        width='auto'
        children={
          <SupportContent>
            <img src={faq} />
            <div>
              <h3>Vous rencontrez un problème sur la plateforme?</h3>
              <p>Contactez notre service</p>
              <h4>Assistance express:</h4>
              <p>
                Nous sommes joignables au 07.66.89.87.12
                <br />
                Du lundi au vendredi de 9:30 à 18:00
              </p>
              <h4>Assistance classique:</h4>
              <p>
                Envoyez un mail à assistance@syope.fr
                <br />
                24/24, 7/7
              </p>
            </div>
          </SupportContent>
        }
      />
    </>
  );
}

function EvalTypeForm({ isOpen, toggleEval, uid }) {
  return (
    <Dialog
      isOpen={isOpen}
      toggleDialog={toggleEval}
      title='Votre avis nous intéresse'
      width='customTypeform'
      children={
        <div
          style={{
            height: 'calc(100% - 57px)',
            width: '100%',
          }}
          className='widget-cntnr'
        >
          <Widget
            id='v1RWsWoN'
            style={{
              height: '100%',
              width: '100%',
            }}
            hidden={{
              user: uid,
            }}
          />
        </div>
      }
    />
  );
}

function Shell({ router, children }) {
  const { currentUser } = useContext(CurrentUserContext);
  const { themeMode, setThemeMode } = useContext(ThemeModeContext);
  const [evalView, setEvalView] = useState(false);
  const [menuView, setMenuView] = useState(false);
  const [openShell, setOpenShell] = useState(false);

  const toggleEval = () => {
    setEvalView(!evalView);
  };

  const setTheme = () => {
    setThemeMode();
  };

  const handleAadSignOut = async ev => {
    ev.preventDefault();
    const signOutUrl = ev.target.pathname || '/auth/sign-in';
    try {
      await http.delete('auth/sign_out');
      localStorage.clear();
      signOut();
      toast.dismiss('aadInfo');
      router.push(signOutUrl);
    } catch (e) {
      if (e instanceof HTTPError && e.response.status === 404) {
        localStorage.clear();
        signOut();
        router.push(signOutUrl);
      } else {
        console.error(e);
      }
    }
  };

  const customAadToast = () => {
    switch (currentUser.departement) {
      case 35:
      case '35':
        return (
          <div>
            Attention, vous devez créer vos bilans avec votre compte Intranet SDIS et les consulter sur la plateforme
            via cette URL :
            <a onClick={ev => handleAadSignOut(ev)} href='/auth/sso-sdis35-aad'>
              https://app.syope.fr/auth/sso-sdis35-aad
            </a>
            Toutes les informations de connexion ici :
            <a
              target='_blank'
              href='https://tootem.sdis35.fr/jcms/ps35i_61995/foire-aux-questions-informatique?id=ps35i_61995&#s35i_1888967'
              rel='noreferrer'
            >
              Tootem SDIS35
            </a>
          </div>
        );
      default:
        return 'Attention, veuillez vous connecter par l’intranet SDIS afin d’accéder à toutes les fonctionnalités';
    }
  };

  if (
    currentUser.provider === 'email' &&
    orgaWithAAD.includes(currentUser.organization_id) &&
    syoUtils.hasPerm(currentUser, 'web:settings:auth:aad-indicateur')
  ) {
    if ([35, '35'].includes(currentUser.departement)) {
      if (!isFormation()) {
        toast(customAadToast, {
          toastId: 'aadInfo',
          type: 'error',
          autoClose: false,
          closeButton: false,
          closeOnClick: false,
        });
      }
    } else {
      toast(customAadToast, {
        toastId: 'aadInfo',
        type: 'error',
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
      });
    }
  }

  const menuItems = [
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:active'),
      icon: <DescriptionIcon />,
      path: '/interventions/active',
      label: 'Interventions actives',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:sso-smp'),
      icon: (
        <svg width='27' height='33' viewBox='0 0 27 33' xmlns='http://www.w3.org/2000/svg'>
          <path d='M4.50008 12.1499C3.73008 12.1499 3.06008 12.0199 2.49008 11.7599C1.92008 11.4999 1.48008 11.1349 1.17008 10.6649C0.860078 10.1949 0.705078 9.6399 0.705078 8.9999H2.95508C2.95508 9.3699 3.09508 9.6649 3.37508 9.8849C3.66508 10.0949 4.05508 10.1999 4.54508 10.1999C5.01508 10.1999 5.38008 10.0949 5.64008 9.8849C5.91008 9.6749 6.04508 9.3849 6.04508 9.0149C6.04508 8.6949 5.94508 8.4199 5.74508 8.1899C5.54508 7.9599 5.26508 7.8049 4.90508 7.7249L3.79508 7.4699C2.86508 7.2499 2.14008 6.8449 1.62008 6.2549C1.11008 5.6549 0.855078 4.9249 0.855078 4.0649C0.855078 3.4249 1.00008 2.8699 1.29008 2.3999C1.59008 1.9199 2.01008 1.5499 2.55008 1.2899C3.09008 1.0299 3.73008 0.899902 4.47008 0.899902C5.59008 0.899902 6.47508 1.1799 7.12508 1.7399C7.78508 2.2899 8.11508 3.0349 8.11508 3.9749H5.86508C5.86508 3.6249 5.74008 3.3499 5.49008 3.1499C5.25008 2.9499 4.90008 2.8499 4.44008 2.8499C4.01008 2.8499 3.68008 2.9499 3.45008 3.1499C3.22008 3.3399 3.10508 3.6149 3.10508 3.9749C3.10508 4.2949 3.19508 4.5699 3.37508 4.7999C3.56508 5.0199 3.83008 5.1699 4.17008 5.2499L5.34008 5.5199C6.31008 5.7399 7.04508 6.1399 7.54508 6.7199C8.04508 7.2899 8.29508 8.0199 8.29508 8.9099C8.29508 9.5499 8.13508 10.1149 7.81508 10.6049C7.50508 11.0949 7.06508 11.4749 6.49508 11.7449C5.93508 12.0149 5.27008 12.1499 4.50008 12.1499Z' />
          <path d='M13.4942 12.1499C12.7242 12.1499 12.0542 12.0199 11.4842 11.7599C10.9142 11.4999 10.4742 11.1349 10.1642 10.6649C9.85422 10.1949 9.69922 9.6399 9.69922 8.9999H11.9492C11.9492 9.3699 12.0892 9.6649 12.3692 9.8849C12.6592 10.0949 13.0492 10.1999 13.5392 10.1999C14.0092 10.1999 14.3742 10.0949 14.6342 9.8849C14.9042 9.6749 15.0392 9.3849 15.0392 9.0149C15.0392 8.6949 14.9392 8.4199 14.7392 8.1899C14.5392 7.9599 14.2592 7.8049 13.8992 7.7249L12.7892 7.4699C11.8592 7.2499 11.1342 6.8449 10.6142 6.2549C10.1042 5.6549 9.84922 4.9249 9.84922 4.0649C9.84922 3.4249 9.99422 2.8699 10.2842 2.3999C10.5842 1.9199 11.0042 1.5499 11.5442 1.2899C12.0842 1.0299 12.7242 0.899902 13.4642 0.899902C14.5842 0.899902 15.4692 1.1799 16.1192 1.7399C16.7792 2.2899 17.1092 3.0349 17.1092 3.9749H14.8592C14.8592 3.6249 14.7342 3.3499 14.4842 3.1499C14.2442 2.9499 13.8942 2.8499 13.4342 2.8499C13.0042 2.8499 12.6742 2.9499 12.4442 3.1499C12.2142 3.3399 12.0992 3.6149 12.0992 3.9749C12.0992 4.2949 12.1892 4.5699 12.3692 4.7999C12.5592 5.0199 12.8242 5.1699 13.1642 5.2499L14.3342 5.5199C15.3042 5.7399 16.0392 6.1399 16.5392 6.7199C17.0392 7.2899 17.2892 8.0199 17.2892 8.9099C17.2892 9.5499 17.1292 10.1149 16.8092 10.6049C16.4992 11.0949 16.0592 11.4749 15.4892 11.7449C14.9292 12.0149 14.2642 12.1499 13.4942 12.1499Z' />
          <path d='M22.4884 12.1499C21.7584 12.1499 21.1234 12.0149 20.5834 11.7449C20.0434 11.4649 19.6234 11.0799 19.3234 10.5899C19.0334 10.0899 18.8884 9.5099 18.8884 8.8499V4.1999C18.8884 3.5399 19.0334 2.9649 19.3234 2.4749C19.6234 1.9749 20.0434 1.5899 20.5834 1.3199C21.1234 1.0399 21.7584 0.899902 22.4884 0.899902C23.2284 0.899902 23.8634 1.0399 24.3934 1.3199C24.9334 1.5899 25.3484 1.9749 25.6384 2.4749C25.9384 2.9649 26.0884 3.5399 26.0884 4.1999V8.8499C26.0884 9.5099 25.9384 10.0899 25.6384 10.5899C25.3484 11.0799 24.9334 11.4649 24.3934 11.7449C23.8634 12.0149 23.2284 12.1499 22.4884 12.1499ZM22.4884 10.1999C22.9384 10.1999 23.2734 10.0849 23.4934 9.8549C23.7234 9.6149 23.8384 9.2799 23.8384 8.8499V4.1999C23.8384 3.7599 23.7284 3.4249 23.5084 3.1949C23.2884 2.9649 22.9484 2.8499 22.4884 2.8499C22.0284 2.8499 21.6884 2.9649 21.4684 3.1949C21.2484 3.4249 21.1384 3.7599 21.1384 4.1999V8.8499C21.1384 9.2799 21.2484 9.6149 21.4684 9.8549C21.6984 10.0849 22.0384 10.1999 22.4884 10.1999Z' />
          <path d='M4.50008 32.1499C3.73008 32.1499 3.06008 32.0199 2.49008 31.7599C1.92008 31.4999 1.48008 31.1349 1.17008 30.6649C0.860078 30.1949 0.705078 29.6399 0.705078 28.9999H2.95508C2.95508 29.3699 3.09508 29.6649 3.37508 29.8849C3.66508 30.0949 4.05508 30.1999 4.54508 30.1999C5.01508 30.1999 5.38008 30.0949 5.64008 29.8849C5.91008 29.6749 6.04508 29.3849 6.04508 29.0149C6.04508 28.6949 5.94508 28.4199 5.74508 28.1899C5.54508 27.9599 5.26508 27.8049 4.90508 27.7249L3.79508 27.4699C2.86508 27.2499 2.14008 26.8449 1.62008 26.2549C1.11008 25.6549 0.855078 24.9249 0.855078 24.0649C0.855078 23.4249 1.00008 22.8699 1.29008 22.3999C1.59008 21.9199 2.01008 21.5499 2.55008 21.2899C3.09008 21.0299 3.73008 20.8999 4.47008 20.8999C5.59008 20.8999 6.47508 21.1799 7.12508 21.7399C7.78508 22.2899 8.11508 23.0349 8.11508 23.9749H5.86508C5.86508 23.6249 5.74008 23.3499 5.49008 23.1499C5.25008 22.9499 4.90008 22.8499 4.44008 22.8499C4.01008 22.8499 3.68008 22.9499 3.45008 23.1499C3.22008 23.3399 3.10508 23.6149 3.10508 23.9749C3.10508 24.2949 3.19508 24.5699 3.37508 24.7999C3.56508 25.0199 3.83008 25.1699 4.17008 25.2499L5.34008 25.5199C6.31008 25.7399 7.04508 26.1399 7.54508 26.7199C8.04508 27.2899 8.29508 28.0199 8.29508 28.9099C8.29508 29.5499 8.13508 30.1149 7.81508 30.6049C7.50508 31.0949 7.06508 31.4749 6.49508 31.7449C5.93508 32.0149 5.27008 32.1499 4.50008 32.1499Z' />
          <path d='M9.62422 31.9999V21.0499H12.1592L13.1642 24.4999C13.2742 24.8599 13.3542 25.1749 13.4042 25.4449C13.4642 25.7149 13.4992 25.9049 13.5092 26.0149C13.5192 25.9049 13.5492 25.7149 13.5992 25.4449C13.6592 25.1749 13.7392 24.8599 13.8392 24.4999L14.8292 21.0499H17.3642V31.9999H15.3092V28.9999C15.3092 28.4499 15.3192 27.8749 15.3392 27.2749C15.3692 26.6649 15.4042 26.0599 15.4442 25.4599C15.4842 24.8599 15.5292 24.2999 15.5792 23.7799C15.6292 23.2499 15.6742 22.7899 15.7142 22.3999L14.4542 27.3499H12.5492L11.2292 22.3999C11.2792 22.7699 11.3292 23.2149 11.3792 23.7349C11.4292 24.2449 11.4742 24.7999 11.5142 25.3999C11.5642 25.9899 11.6042 26.5949 11.6342 27.2149C11.6642 27.8349 11.6792 28.4299 11.6792 28.9999V31.9999H9.62422Z' />
          <path d='M18.9784 31.9999V21.0499H22.7134C23.4834 21.0499 24.1534 21.1949 24.7234 21.4849C25.3034 21.7749 25.7484 22.1799 26.0584 22.6999C26.3784 23.2199 26.5384 23.8299 26.5384 24.5299C26.5384 25.2199 26.3784 25.8299 26.0584 26.3599C25.7384 26.8799 25.2934 27.2849 24.7234 27.5749C24.1534 27.8649 23.4834 28.0099 22.7134 28.0099H21.2284V31.9999H18.9784ZM21.2284 25.9849H22.7134C23.1934 25.9849 23.5734 25.8549 23.8534 25.5949C24.1434 25.3249 24.2884 24.9699 24.2884 24.5299C24.2884 24.0899 24.1434 23.7399 23.8534 23.4799C23.5734 23.2099 23.1934 23.0749 22.7134 23.0749H21.2284V25.9849Z' />
        </svg>
      ),
      path: '/interventions/sso-smp',
      label: 'Interventions SSO SMP',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:archived'),
      icon: <FolderIcon />,
      path: syoUtils.hasPerm(currentUser, 'web:interventions_inactive:listing:last_two_week')
        ? `/interventions/archive?createdAfter=${DateTime.now().plus({ weeks: -2 }).toISODate()}&createdBefore=${DateTime.now().toISODate()}&createdEntity=bilans&page=1`
        : '/interventions/archive',
      label: 'Interventions archivées',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:sso-smp:archived'),
      icon: (
        <svg width='22' height='20' viewBox='0 0 20 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            fill-rule='evenodd'
            clip-rule='evenodd'
            d='M2 0H8L10 2H18C19.1 2 20 2.9 20 4V14C20 15.1 19.1 16 18 16H2C0.9 16 0 15.1 0 14L0.00999999 2C0.00999999 0.9 0.9 0 2 0ZM8.69097 8.23011C8.91161 8.33139 9.17097 8.38202 9.46904 8.38202C9.7671 8.38202 10.0245 8.32944 10.2413 8.22427C10.4619 8.1191 10.6323 7.97109 10.7523 7.78022C10.8761 7.58936 10.9381 7.36929 10.9381 7.12C10.9381 6.77333 10.8413 6.48899 10.6477 6.26697C10.4542 6.04105 10.1697 5.88524 9.7942 5.79955L9.34129 5.69438C9.20968 5.66322 9.1071 5.60479 9.03355 5.5191C8.96387 5.42951 8.92903 5.3224 8.92903 5.19775C8.92903 5.05753 8.97355 4.95041 9.06258 4.8764C9.15162 4.7985 9.27936 4.75955 9.44581 4.75955C9.62387 4.75955 9.75936 4.7985 9.85226 4.8764C9.94904 4.95431 9.99742 5.06142 9.99742 5.19775H10.8684C10.8684 4.83161 10.7407 4.54142 10.4852 4.32719C10.2336 4.10906 9.89097 4 9.45742 4C9.17097 4 8.92323 4.05064 8.7142 4.15191C8.50516 4.25318 8.34258 4.3973 8.22645 4.58427C8.11419 4.76734 8.05806 4.98352 8.05806 5.23281C8.05806 5.56779 8.15677 5.85213 8.35419 6.08584C8.55548 6.31566 8.83613 6.47341 9.19613 6.5591L9.62581 6.65843C9.76517 6.68959 9.87355 6.74996 9.95097 6.83955C10.0284 6.92914 10.0671 7.03625 10.0671 7.1609C10.0671 7.30502 10.0148 7.41798 9.91033 7.49977C9.80968 7.58157 9.66839 7.62247 9.48645 7.62247C9.29678 7.62247 9.14581 7.58157 9.03355 7.49977C8.92516 7.41408 8.87097 7.29918 8.87097 7.15506H8C8 7.40434 8.06 7.62052 8.18 7.8036C8.3 7.98667 8.47032 8.12884 8.69097 8.23011ZM12.1726 8.23011C12.3932 8.33139 12.6526 8.38202 12.9506 8.38202C13.2487 8.38202 13.5061 8.32944 13.7229 8.22427C13.9436 8.1191 14.1139 7.97109 14.2339 7.78022C14.3577 7.58936 14.4197 7.36929 14.4197 7.12C14.4197 6.77333 14.3229 6.48899 14.1294 6.26697C13.9358 6.04105 13.6513 5.88524 13.2758 5.79955L12.8229 5.69438C12.6913 5.66322 12.5887 5.60479 12.5152 5.5191C12.4455 5.42951 12.4106 5.3224 12.4106 5.19775C12.4106 5.05753 12.4552 4.95041 12.5442 4.8764C12.6332 4.7985 12.761 4.75955 12.9274 4.75955C13.1055 4.75955 13.241 4.7985 13.3339 4.8764C13.4306 4.95431 13.479 5.06142 13.479 5.19775H14.35C14.35 4.83161 14.2223 4.54142 13.9668 4.32719C13.7152 4.10906 13.3726 4 12.939 4C12.6526 4 12.4048 4.05064 12.1958 4.15191C11.9868 4.25318 11.8242 4.3973 11.7081 4.58427C11.5958 4.76734 11.5397 4.98352 11.5397 5.23281C11.5397 5.56779 11.6384 5.85213 11.8358 6.08584C12.0371 6.31566 12.3177 6.47341 12.6777 6.5591L13.1074 6.65843C13.2468 6.68959 13.3552 6.74996 13.4326 6.83955C13.51 6.92914 13.5487 7.03625 13.5487 7.1609C13.5487 7.30502 13.4965 7.41798 13.3919 7.49977C13.2913 7.58157 13.15 7.62247 12.9681 7.62247C12.7784 7.62247 12.6274 7.58157 12.5152 7.49977C12.4068 7.41408 12.3526 7.29918 12.3526 7.15506H11.4816C11.4816 7.40434 11.5416 7.62052 11.6616 7.8036C11.7816 7.98667 11.9519 8.12884 12.1726 8.23011ZM16.4323 8.38202C16.1497 8.38202 15.9039 8.32944 15.6948 8.22427C15.4858 8.11521 15.3232 7.96524 15.2071 7.77438C15.0948 7.57963 15.0387 7.35371 15.0387 7.09663V5.28539C15.0387 5.02831 15.0948 4.80434 15.2071 4.61348C15.3232 4.41873 15.4858 4.26876 15.6948 4.1636C15.9039 4.05453 16.1497 4 16.4323 4C16.7187 4 16.9645 4.05453 17.1697 4.1636C17.3787 4.26876 17.5394 4.41873 17.6516 4.61348C17.7677 4.80434 17.8258 5.02831 17.8258 5.28539V7.09663C17.8258 7.35371 17.7677 7.57963 17.6516 7.77438C17.5394 7.96524 17.3787 8.11521 17.1697 8.22427C16.9645 8.32944 16.7187 8.38202 16.4323 8.38202ZM16.4323 7.62247C16.6064 7.62247 16.7361 7.57768 16.8213 7.48809C16.9103 7.39461 16.9548 7.26412 16.9548 7.09663V5.28539C16.9548 5.11401 16.9123 4.98352 16.8271 4.89393C16.7419 4.80434 16.6103 4.75955 16.4323 4.75955C16.2542 4.75955 16.1226 4.80434 16.0374 4.89393C15.9523 4.98352 15.9097 5.11401 15.9097 5.28539V7.09663C15.9097 7.26412 15.9523 7.39461 16.0374 7.48809C16.1264 7.57768 16.2581 7.62247 16.4323 7.62247ZM8.69097 13.8481C8.91161 13.9494 9.17097 14 9.46904 14C9.7671 14 10.0245 13.9474 10.2413 13.8422C10.4619 13.7371 10.6323 13.5891 10.7523 13.3982C10.8761 13.2073 10.9381 12.9873 10.9381 12.738C10.9381 12.3913 10.8413 12.107 10.6477 11.8849C10.4542 11.659 10.1697 11.5032 9.7942 11.4175L9.34129 11.3124C9.20968 11.2812 9.1071 11.2228 9.03355 11.1371C8.96387 11.0475 8.92903 10.9404 8.92903 10.8157C8.92903 10.6755 8.97355 10.5684 9.06258 10.4944C9.15162 10.4165 9.27936 10.3775 9.44581 10.3775C9.62387 10.3775 9.75936 10.4165 9.85226 10.4944C9.94904 10.5723 9.99742 10.6794 9.99742 10.8157H10.8684C10.8684 10.4496 10.7407 10.1594 10.4852 9.94517C10.2336 9.72704 9.89097 9.61798 9.45742 9.61798C9.17097 9.61798 8.92323 9.66861 8.7142 9.76989C8.50516 9.87116 8.34258 10.0153 8.22645 10.2022C8.11419 10.3853 8.05806 10.6015 8.05806 10.8508C8.05806 11.1858 8.15677 11.4701 8.35419 11.7038C8.55548 11.9336 8.83613 12.0914 9.19613 12.1771L9.62581 12.2764C9.76517 12.3076 9.87355 12.3679 9.95097 12.4575C10.0284 12.5471 10.0671 12.6542 10.0671 12.7789C10.0671 12.923 10.0148 13.036 9.91033 13.1178C9.80968 13.1996 9.66839 13.2404 9.48645 13.2404C9.29678 13.2404 9.14581 13.1996 9.03355 13.1178C8.92516 13.0321 8.87097 12.9172 8.87097 12.773H8C8 13.0223 8.06 13.2385 8.18 13.4216C8.3 13.6046 8.47032 13.7468 8.69097 13.8481ZM11.4526 13.9416V9.6764H12.4339L12.8229 11.0202C12.8655 11.1604 12.8965 11.2831 12.9158 11.3883C12.939 11.4935 12.9526 11.5675 12.9565 11.6103C12.9603 11.5675 12.9719 11.4935 12.9913 11.3883C13.0145 11.2831 13.0455 11.1604 13.0842 11.0202L13.4674 9.6764H14.4487V13.9416H13.6532V12.773C13.6532 12.5588 13.6571 12.3348 13.6648 12.1011C13.6765 11.8635 13.69 11.6279 13.7055 11.3942C13.721 11.1604 13.7384 10.9423 13.7577 10.7398C13.7771 10.5333 13.7945 10.3542 13.81 10.2022L13.3223 12.1303H12.5848L12.0739 10.2022C12.0932 10.3464 12.1126 10.5197 12.1319 10.7222C12.1513 10.9209 12.1687 11.1371 12.1842 11.3708C12.2035 11.6006 12.219 11.8363 12.2306 12.0778C12.2423 12.3193 12.2481 12.551 12.2481 12.773V13.9416H11.4526ZM15.0735 9.6764V13.9416H15.9445V12.3874H16.5194C16.8174 12.3874 17.0768 12.3309 17.2974 12.218C17.5181 12.105 17.6903 11.9473 17.8142 11.7447C17.9381 11.5383 18 11.3007 18 11.0319C18 10.7593 17.9381 10.5216 17.8142 10.3191C17.6942 10.1166 17.5219 9.9588 17.2974 9.84584C17.0768 9.73288 16.8174 9.6764 16.5194 9.6764H15.0735ZM16.5194 11.5987H15.9445V10.4652H16.5194C16.7052 10.4652 16.8523 10.5178 16.9606 10.6229C17.0729 10.7242 17.129 10.8605 17.129 11.0319C17.129 11.2033 17.0729 11.3416 16.9606 11.4467C16.8523 11.548 16.7052 11.5987 16.5194 11.5987Z'
          />
        </svg>
      ),
      path: '/interventions/sso-smp-archive',
      label: 'Interventions SSO SMP archivées',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'mobilisations:listing'),
      icon: <LeakAddIcon />,
      path: '/interventions/mobilisations',
      label: 'Mobilisations',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:dispatch'),
      icon: <AddIcon />,
      path: '/interventions/declenchement',
      label: 'Déclencher une intervention',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:inactive_with_injections'),
      icon: <ColorizeIcon />,
      path: '/interventions/pui?only_with_injections=true',
      label: 'Interventions PUI',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'interventions:inactive_with_abandonment'),
      icon: <ArchiveIcon />,
      path: '/interventions/abandon',
      label: 'Abandon matériels',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'users:list'),
      icon: <GroupIcon />,
      path: '/utilisateurs',
      label: 'Utilisateurs',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'licenses:manage'),
      icon: <DevicesIcon />,
      path: '/licenses',
      label: 'Licences',
    },
    {
      perm: () => hasLegacyPerm(currentUser, 'manage:organization'),
      icon: <SettingsIcon />,
      path: '/gestionnaire',
      label: "Gestionnaire d'organisation",
    },
  ];

  const menu = useMemo(() => {
    let temp = [];
    menuItems.map(item => {
      if (item.perm()) temp.push(item);
    });
    return temp;
  }, [currentUser]);

  const toggleMenu = () => setMenuView(!menuView);

  return (
    <>
      <SideNav className={openShell ? 'open' : 'close'}>
        <SideNavContent>
          <LogoContainer>
            <Img className='logo' src={logo} style={{ filter: isFormation() ? 'invert(1)' : 'none' }} />
            {openShell && <p>Syopé</p>}
          </LogoContainer>
          <ButtonNav type='button' onClick={() => setOpenShell(!openShell)}>
            <ExpandLessIcon className={`icon ${openShell ? 'open' : 'close'}`} />
          </ButtonNav>
        </SideNavContent>
        <SideNavContentScrollable className='scrollbarStyle'>
          <>
            {menu.map((item, idx) => (
              <PageLink key={idx} to={item.path} activeClassName='active' aria-label={item.label}>
                <IconContainer>{item.icon}</IconContainer>
                {openShell && <p>{item.label}</p>}
              </PageLink>
            ))}
          </>
        </SideNavContentScrollable>
        <SideNavContent className='end'>
          <Support aria-label={themeMode === 'dark' ? 'Thème clair' : 'Thème sombre'} onClick={() => setTheme()}>
            {themeMode === 'dark' ? (
              <IconContainerSide>
                <Brightness4Icon />
              </IconContainerSide>
            ) : (
              <IconContainerSide>
                <Brightness4OutlinedIcon />
              </IconContainerSide>
            )}
            {openShell && <p>Thème</p>}
          </Support>
          <SupportOnline openShell={openShell} />
          <Support aria-label='Votre avis nous intéresse' onClick={() => toggleEval()}>
            <IconContainerSide>
              <GradeIcon />
            </IconContainerSide>
            {openShell && <p>Votre avis nous intéresse</p>}
          </Support>
          <Support>
            <PageLink to='/reglages'>
              <UserMenu to='/reglages'>
                <span>
                  {currentUser.full_name
                    .split(' ', 2)
                    .map(s => s[0])
                    .join('')
                    .toUpperCase()}
                </span>
              </UserMenu>
              {openShell && <p>Mon compte</p>}
            </PageLink>
          </Support>
        </SideNavContent>
      </SideNav>

      <SideNavMobile menuIsOpen={menuView}>
        <SideNavContentLogoAltMobile>
          <Img className='logo' src={logo} />
        </SideNavContentLogoAltMobile>
        <SideNavContentMobile className='scrollable'>
          {menu.map((item, idx) => (
            <PageLinkMobile
              key={idx}
              to={item.path}
              activeClassName='active'
              aria-label={item.label}
              onClick={() => setMenuView(false)}
            >
              <IconContainer>{item.icon}</IconContainer>
              {item.label}
            </PageLinkMobile>
          ))}
        </SideNavContentMobile>
        <SideNavContentAltMobile>
          <SupportMobile aria-label={themeMode === 'dark' ? 'Thème clair' : 'Thème sombre'} onClick={() => setTheme()}>
            {themeMode === 'dark' ? <Brightness4Icon /> : <Brightness4OutlinedIcon />}
          </SupportMobile>
          <SupportOnline openShell={openShell} mobile={true} />
          <UserMenu to='/reglages' onClick={() => setMenuView(false)}>
            <span>
              {currentUser.full_name
                .split(' ', 2)
                .map(s => s[0])
                .join('')
                .toUpperCase()}
            </span>
          </UserMenu>
        </SideNavContentAltMobile>
      </SideNavMobile>
      <ToggleMenu onClick={() => toggleMenu()}>
        <span>{menuView ? <CloseIcon /> : <MenuIcon />}</span>
      </ToggleMenu>
      <ScrollArrow />
      <FlexContainer className={openShell ? 'openShell' : 'closeShell'}>
        <Content>{children}</Content>
        <EvalTypeForm isOpen={evalView} toggleEval={() => toggleEval()} uid={currentUser.uid} />
      </FlexContainer>
      <SessionTimeout />
    </>
  );
}

export default Shell;

const ScrollTopCntnr = styled(ToggleMenu)`
  left: unset;
  right: ${theme.thin};
  z-index: 11;
`;

const ScrollArrow = () => {
  const [showScroll, setShowScroll] = useState(false);

  const checkScrollTop = () => {
    if (!showScroll && window.scrollY > 500) {
      setShowScroll(true);
    } else if (showScroll && window.scrollY <= 500) {
      setShowScroll(false);
    }
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  window.addEventListener('scroll', checkScrollTop);

  return (
    <ScrollTopCntnr className='scrollTop' onClick={scrollTop} style={{ display: showScroll ? 'block' : 'none' }}>
      <ArrowUpwardIcon />
    </ScrollTopCntnr>
  );
};
